import React, { useEffect, useRef, useState } from 'react';
import { Form, Input, Modal, Select, Typography } from 'antd';
import { makePrioStyles } from '../../../theme/utils';
import { useTranslation } from 'react-i18next';
import { EmailString, ProjectId } from '../../../models/Types';

import {
  EmailSearchResult,
  EmailSearchResultContact,
  EmailSearchResultProject,
} from '../../../models/Message';
import useAddressSearch from '../hooks/useAddressSearch';
import { CustomTagProps } from 'rc-select/lib/interface/generator';
import TransBtn from 'rc-select/lib/TransBtn';
import classNames from 'classnames';
import { CloseOutlined } from '@ant-design/icons';
import { emailPattern } from '../../../hooks/useEmailValidation';
import { useForm } from 'antd/lib/form/Form';
import useDebounce from '../../../hooks/useDebounce';
import { useDrag, useDrop } from 'react-dnd';
import { DndMailAddressDto, DND_TYPE_EMAIL_ADDRESS } from '../../../dnd/types';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { Classes } from 'jss';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../theme/types';
import { apiDeleteEmailSuggestion } from '../api';
import { debounceFunction } from '../../../util';

const useStylesAddressSelectMail = makePrioStyles((theme) => ({
  root: {
    flex: 1,
    overflow: 'hidden',
    '& .ant-select-selection-item-content .secondary': {
      display: 'none',
    },
    '& .ant-form-item-explain': {
      display: 'none',
    },
    '& .ant-select-selector $emailSuggestionRow .anticon.anticon-close': {
      display: 'none',
    },
  },
  flex1: {
    flex: 1,
    overflow: 'hidden',
    '& .ant-select-selection-item-content .secondary': {
      display: 'none',
    },
  },

  fullWidth: {
    width: '100%',
  },
  loadingOverlay: {
    background: '#FFFFFFBF',
    position: 'absolute',
    top: 0,
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  popover: {
    '& .ant-popover-inner-content': {
      maxHeight: 500,
      maxWidth: 400,
      overflowY: 'scroll',
    },
  },
  secondary: {
    color: theme.old.typography.colors.muted,
  },
  tag: {
    '& .secondary': {
      display: 'none',
    },
  },
  invalidEmailTag: {
    color: `${theme.old.palette.chromaticPalette.red} !important`,
    backgroundColor: `${theme.old.palette.chromaticPalette.red}15 !important`,
    borderColor: `${theme.old.palette.chromaticPalette.red} !important`,
  },
  invalidEmailTagContent: {
    color: `${theme.old.palette.chromaticPalette.red} !important`,
    fontWeight: 500,
  },
  suggestionLabel: {
    fontSize: theme.old.typography.fontSize.tiny,
    color: theme.old.typography.colors.muted,
    padding: `${theme.old.spacing.unit(1)}px 0`,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  suggestionItem: {
    paddingLeft: `${theme.old.spacing.unit(2)}px`,
  },
  showAllButton: {
    fontSize: theme.old.typography.fontSize.tiny,
    color: theme.old.palette.chromaticPalette.blue,
    padding: `${theme.old.spacing.unit(0.5)}px 0 ${theme.old.spacing.unit(0.5)}px ${theme.old.spacing.unit(2)}px`,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.old.palette.backgroundPalette.hover,
    },
  },
  loadingIndicator: {
    padding: `${theme.old.spacing.unit(1)}px ${theme.old.spacing.unit(1.5)}px`,
    textAlign: 'center',
    color: theme.old.typography.colors.muted,
  },
  roleText: {
    fontSize: theme.old.typography.fontSize.tiny,
    color: theme.old.typography.colors.muted,
    marginLeft: theme.old.spacing.unit(0.5),
    fontWeight: 400,
  },
  suggestion: {
    backgroundColor: theme.old.palette.backgroundPalette.content,
    borderBottom: theme.old.borders.content,
  },
  emailSuggestionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingRight: theme.spacing.extraSmall,
    '& .anticon-close': {
      visibility: 'hidden',
    },
    '&:hover .anticon-close': {
      visibility: 'visible',
    },
  },
  search: {
    '&.ant-input-search > .ant-input-group > .ant-input-group-addon:last-child':
      {
        border: theme.old.borders.content,
        '& .ant-input-search-button': {
          height: '100%',
          padding: `0 ${theme.old.spacing.unit(1.5)}px`,
          background: 'transparent',
        },
        '&:hover': {
          borderColor: 'var(--ant-primary-5)',
        },
      },
    '&.ant-input-search-with-button .ant-input-affix-wrapper:not(.ant-input-affix-wrapper-disabled):hover':
      {
        zIndex: 1,
      },
    '& .ant-input-group': {
      display: 'flex',
      flexDirection: 'row',
      overflow: 'hidden',
      height: 32,
    },
    '& .ant-input-group-addon': { padding: 0, display: 'block' },
    '& .ant-input-group-addon:first-child': { width: 125 },
    '& .ant-input-group-addon:last-child': { width: 50 },
    '& .ant-input-group-addon .ant-select': {
      margin: 0,
    },
    '& .ant-input-affix-wrapper': {
      padding: `0 11px`,
    },
    '& .ant-input-prefix': {
      overflowX: 'scroll',
      overflowY: 'hidden',
      maxWidth: 'calc(100% - 52.125px)',
      paddingTop: 4,
    },
    '& .ant-input-affix-wrapper > input.ant-input': {
      minWidth: 4,
      padding: `4px 0`,
    },
    '& .ant-input-group-addon .ant-select.ant-select-single:not(.ant-select-customize-input) .ant-select-selector':
      {
        height: 30,
      },
    '& .ant-select-single .ant-select-selector .ant-select-selection-item': {
      lineHeight: '28px',
    },
  },
}));

const emailSearchResultToHTMLText = (
  value: EmailSearchResult,
  classes: Classes
) => {
  if (
    value.type === 'contact' ||
    ((value as EmailSearchResultContact).firstName &&
      (value as EmailSearchResultContact).lastName)
  ) {
    const contact = value as EmailSearchResultContact;

    if (contact.lastName && contact.firstName) {
      return (
        <span>
          <span style={{ fontWeight: 500 }}>
            {`${contact?.firstName} ${contact?.lastName}`}
          </span>{' '}
          <span className={classNames('secondary', classes.secondary)}>
            {`<${contact.eMail}>`}
          </span>
        </span>
      );
    }
  } else if (
    value.type === 'project' ||
    (value as EmailSearchResultProject).projectName
  ) {
    const project = value as EmailSearchResultProject;
    if (project.projectName) {
      return (
        <span>
          <span style={{ fontWeight: 500 }}>{project.projectName}</span>{' '}
          <span className={classNames('secondary', classes.secondary)}>
            {`<${project.eMail}>`}
          </span>
        </span>
      );
    }
  }
  return <span className={classes.secondary}>{value.eMail}</span>;
};

const debounceSetSearchTerm = debounceFunction(
  (value: string, setSearchTerm: (value: string) => void) => {
    setSearchTerm(value);
  },
  350
);

// Helper function to extract email from strings like "Max Muster <max@muster.de>"
const extractEmailFromString = (input: string): string | null => {
  // Try to extract email from format "Name <email@domain.com>"
  const emailInBracketsMatch = input.match(/<([^>]+)>/);
  if (emailInBracketsMatch) {
    const extracted = emailInBracketsMatch[1].trim();
    // Validate extracted email
    return extracted.match(emailPattern) ? extracted : null;
  }

  // Try to find email pattern in the string
  const emailMatch = input.match(/[\w.-]+@[\w.-]+\.\w+/);
  if (emailMatch) {
    const extracted = emailMatch[0].trim();
    // Validate extracted email
    return extracted.match(emailPattern) ? extracted : null;
  }

  // Check if input itself is a valid email
  const trimmed = input.trim();
  return trimmed.match(emailPattern) ? trimmed : null;
};

const normalizeEmailInput = (emails: string[]): string[] => {
  const normalized = emails.map((email) => {
    const extracted = extractEmailFromString(email);
    return extracted !== null ? extracted : email.trim();
  });

  return Array.from(new Set(normalized));
};

interface AddressSelectMailProps {
  value?: string[];
  changeHandler?: (value: EmailString[]) => void;
  focus?: boolean;
  bordered?: boolean;
  maxCount?: number | 'responsive';
  open?: boolean;
  closableTags?: boolean;
  projectId: ProjectId;
  excludedMails?: EmailString[];
  tabIndex?: number;
}

const AddressSelectMail: React.FC<AddressSelectMailProps> = React.memo(
  (props) => {
    //#region ------------------------------ Defaults
    const {
      value,
      changeHandler,
      bordered,
      open,
      closableTags,
      projectId,
      tabIndex,
      focus,
    } = props;
    const classes = useStylesAddressSelectMail(props);
    const { t } = useTranslation();

    const [form] = useForm();
    const [modalForm] = useForm();
    const theme = useTheme<PrioTheme>();
    //#endregion

    //#region ------------------------------ States / Attributes / Selectors
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [dropdownFocus, setDropdownFocus] = useState<boolean>(open);

    const selectRef = useRef(null);

    const {
      addresses,
      suggestions,
      internalProjectContacts,
      externalProjectContacts,
      additionalContacts,
      isLoadingAdditionalContacts,
      refreshAddressSuggestions,
    } = useAddressSearch(searchTerm, projectId, value);

    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

    const [selectedMail, setSelectedMail] = useState<string>('');
    const [renameMailinputValue, setRenameMailinputValue] =
      useState<string>('');
    const debouncedRenameMailinputValue = useDebounce(
      renameMailinputValue,
      100
    );
    const inputRef = useRef<Input>(null);

    const [showAllSuggestions, setShowAllSuggestions] = useState(false);
    const [showAllProjectContacts, setShowAllProjectContacts] = useState(false);
    const [showAllAdditionalContacts, setShowAllAdditionalContacts] =
      useState(false);

    const MAX_ITEMS = 20;
    //#endregion

    //#region ------------------------------ Methods / Handlers
    const handleOk = () => {
      let i = value.findIndex((email) => email === selectedMail);
      let _value = [...value];
      _value[i] = renameMailinputValue;
      changeHandler(_value);
      setIsModalVisible(false);
      setRenameMailinputValue('');
      setSelectedMail('');
      form.validateFields();
    };

    const handleCancel = () => {
      setIsModalVisible(false);
      setRenameMailinputValue('');
      setSelectedMail('');
    };

    const showModal = (mail: string) => {
      setIsModalVisible(true);
      setRenameMailinputValue(mail);
      setSelectedMail(mail);
      modalForm.setFieldsValue({ renameMailInput: mail });
    };

    const onMailSuggestionDelete = async (email: string) => {
      await apiDeleteEmailSuggestion(projectId, email);
      await refreshAddressSuggestions();
    };

    // Helper function to render grouped options
    const renderGroupedOptions = () => {
      const options = [];

      // Get all selected values - only show when no search term
      if (value.length > 0 && !searchTerm) {
        options.push(
          <Select.Option
            key="selected-label"
            value="__selected_label__"
            disabled
          >
            <Typography.Text className={classes.suggestionLabel}>
              {t('contacts:actions.selected')}
            </Typography.Text>
          </Select.Option>
        );

        // Add all selected values, color invalid ones red
        value.forEach((email) => {
          const address = addresses.find((addr) => addr.eMail === email);
          const isValidEmail = email.match(emailPattern);

          options.push(
            <Select.Option
              key={`selected-${email}`}
              value={email}
              data={address}
            >
              <div className={classNames(classes.emailSuggestionRow)}>
                <div
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                    color: isValidEmail
                      ? undefined
                      : theme.old.palette.chromaticPalette.red,
                  }}
                >
                  {address ? (
                    emailSearchResultToHTMLText(address, classes)
                  ) : (
                    <span>{email}</span>
                  )}
                </div>
              </div>
            </Select.Option>
          );
        });
      }

      // Suggestions group (exclude selected)
      const unselectedSuggestions = suggestions.filter(
        (s) => !value.includes(s.eMail)
      );
      if (unselectedSuggestions.length > 0) {
        options.push(
          <Select.Option
            key="suggestions-label"
            value="__suggestions_label__"
            disabled
          >
            <Typography.Text className={classes.suggestionLabel}>
              {t('contacts:actions.suggestions')}
            </Typography.Text>
          </Select.Option>
        );

        const displayedSuggestions = showAllSuggestions
          ? unselectedSuggestions
          : unselectedSuggestions.slice(0, MAX_ITEMS);

        displayedSuggestions.forEach((address) =>
          options.push(
            <Select.Option
              key={`suggestion-${address.id}-${address.eMail}`}
              value={address.eMail}
              data={address}
            >
              <div
                className={classNames(
                  classes.emailSuggestionRow,
                  classes.suggestionItem
                )}
              >
                <div
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                  }}
                >
                  {emailSearchResultToHTMLText(address, classes)}
                </div>
                {address.eMail && address.isSuggestion && (
                  <CloseOutlined
                    onClick={async (e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      await onMailSuggestionDelete(address.eMail);
                    }}
                  />
                )}
              </div>
            </Select.Option>
          )
        );

        if (!showAllSuggestions && unselectedSuggestions.length > MAX_ITEMS) {
          options.push(
            <Select.Option
              key="show-all-suggestions"
              value="__show_all_suggestions__"
              disabled
            >
              <div
                className={classes.showAllButton}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAllSuggestions(true);
                }}
              >
                {t('contacts:actions.showAll')} ({unselectedSuggestions.length})
              </div>
            </Select.Option>
          );
        }
      }

      // Project contacts group (internal + external, exclude selected)
      const unselectedProjectContacts = [
        ...internalProjectContacts,
        ...externalProjectContacts,
      ].filter((c) => !value.includes(c.eMail));

      if (unselectedProjectContacts.length > 0 && projectId !== 'me') {
        options.push(
          <Select.Option
            key="project-contacts-label"
            value="__project_contacts_label__"
            disabled
          >
            <Typography.Text className={classes.suggestionLabel}>
              {t('contacts:actions.projectContacts')}
            </Typography.Text>
          </Select.Option>
        );

        const displayedProjectContacts = showAllProjectContacts
          ? unselectedProjectContacts
          : unselectedProjectContacts.slice(0, MAX_ITEMS);

        displayedProjectContacts.forEach((address) =>
          options.push(
            <Select.Option
              key={`project-contact-${address.id}-${address.eMail}`}
              value={address.eMail}
              data={address}
            >
              <div
                className={classNames(
                  classes.emailSuggestionRow,
                  classes.suggestionItem
                )}
              >
                <div
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                  }}
                >
                  {emailSearchResultToHTMLText(address, classes)}
                </div>
              </div>
            </Select.Option>
          )
        );

        if (
          !showAllProjectContacts &&
          unselectedProjectContacts.length > MAX_ITEMS
        ) {
          options.push(
            <Select.Option
              key="show-all-project-contacts"
              value="__show_all_project_contacts__"
              disabled
            >
              <div
                className={classes.showAllButton}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAllProjectContacts(true);
                }}
              >
                {t('contacts:actions.showAll')} (
                {unselectedProjectContacts.length})
              </div>
            </Select.Option>
          );
        }
      }

      // Additional contacts group (from backend search)
      if (searchTerm && searchTerm.length > 2) {
        if (isLoadingAdditionalContacts) {
          options.push(
            <Select.Option
              key="additional-contacts-label"
              value="__additional_label__"
              disabled
            >
              <Typography.Text className={classes.suggestionLabel}>
                {t('contacts:actions.additionalContacts')}
              </Typography.Text>
            </Select.Option>
          );
          options.push(
            <Select.Option
              key="loading-additional"
              value="__loading__"
              disabled
            >
              <div className={classes.loadingIndicator}>
                {t('contacts:actions.loading')}
              </div>
            </Select.Option>
          );
        } else {
          // Additional contacts (exclude selected)
          const unselectedAdditionalContacts = additionalContacts.filter(
            (c) => !value.includes(c.eMail)
          );
          if (unselectedAdditionalContacts.length > 0) {
            options.push(
              <Select.Option
                key="additional-contacts-label-2"
                value="__additional_label_2__"
                disabled
              >
                <Typography.Text className={classes.suggestionLabel}>
                  {t('contacts:actions.additionalContacts')}
                </Typography.Text>
              </Select.Option>
            );

            const displayedAdditionalContacts = showAllAdditionalContacts
              ? unselectedAdditionalContacts
              : unselectedAdditionalContacts.slice(0, MAX_ITEMS);

            displayedAdditionalContacts.forEach((address) =>
              options.push(
                <Select.Option
                  key={`additional-${address.id}-${address.eMail}`}
                  value={address.eMail}
                  data={address}
                >
                  <div
                    className={classNames(
                      classes.emailSuggestionRow,
                      classes.suggestionItem
                    )}
                  >
                    <div
                      style={{
                        flex: 1,
                        overflow: 'hidden',
                      }}
                    >
                      {emailSearchResultToHTMLText(address, classes)}
                    </div>
                  </div>
                </Select.Option>
              )
            );

            if (
              !showAllAdditionalContacts &&
              unselectedAdditionalContacts.length > MAX_ITEMS
            ) {
              options.push(
                <Select.Option
                  key="show-all-additional"
                  value="__show_all_additional__"
                  disabled
                >
                  <div
                    className={classes.showAllButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAllAdditionalContacts(true);
                    }}
                  >
                    {t('contacts:actions.showAll')} (
                    {unselectedAdditionalContacts.length})
                  </div>
                </Select.Option>
              );
            }
          }
        }
      }

      return options;
    };
    //#endregion

    //#region ------------------------------ Effects
    const [, dropRef] = useDrop<DndMailAddressDto, any, any>({
      accept: [DND_TYPE_EMAIL_ADDRESS],
      drop: (item, monitor) => {
        const type = monitor.getItemType();
        if (monitor.didDrop()) {
          return undefined;
        }
        switch (type) {
          case DND_TYPE_EMAIL_ADDRESS:
            if (!value.includes(item.value)) {
              changeHandler([...value, item.value]);
            }
            return { close: true };
          default:
            return undefined;
        }
      },
      canDrop: (item) => !value.includes(item.value),
    });

    useEffect(() => {
      setSearchTerm('');
      form.setFieldsValue({
        addressSelect: value,
      });
      form.validateFields();
      // setTagEmails(value);
    }, [value, form]);
    //#endregion

    return (
      <div className={classes.root} ref={dropRef}>
        <Form
          form={form}
          onValuesChange={(currentValues) => {
            if (currentValues.addressSelect) {
              if (changeHandler) {
                // Normalize email addresses before passing them to the handler
                const normalizedEmails = normalizeEmailInput(
                  currentValues.addressSelect
                );

                // Check if normalization changed any values
                const hasChanged = normalizedEmails.some(
                  (email, index) => email !== currentValues.addressSelect[index]
                );

                // Update form field if values were normalized
                if (hasChanged) {
                  form.setFieldsValue({ addressSelect: normalizedEmails });
                }

                changeHandler(normalizedEmails);
              }
            }
          }}
        >
          <Form.Item
            name="addressSelect"
            style={{ marginBottom: 0 }}
            rules={[
              () => ({
                async validator(rule, values) {
                  const validMails = value.filter((email) =>
                    email.match(emailPattern)
                  );
                  if (validMails.length === value.length) {
                    return Promise.resolve();
                  }
                  return Promise.reject();
                },
              }),
            ]}
          >
            <Select<EmailString[]>
              ref={selectRef}
              mode="tags"
              maxTagCount={7}
              bordered={bordered ?? false}
              tokenSeparators={['; ', ';', ',']}
              open={dropdownFocus}
              onBlur={(e) => {
                setDropdownFocus(false);
              }}
              autoFocus={focus}
              onMouseDown={(e) => {
                e.preventDefault();
                return false;
              }}
              onSearch={(value: string) => {
                debounceSetSearchTerm(value, setSearchTerm);
                // Reset show all states when user starts typing
                if (value) {
                  setShowAllSuggestions(false);
                  setShowAllProjectContacts(false);
                  setShowAllAdditionalContacts(false);
                }
              }}
              dropdownRender={(menu) => menu}
              filterOption={() => {
                return true;
              }}
              tagRender={(props: CustomTagProps) => (
                <TagRenderer
                  showModal={showModal}
                  closable={closableTags}
                  selectedValues={value}
                  classes={classes}
                  {...props}
                />
              )}
              onSelect={(selectedValue) => {
                // Ignore special values for "show all" buttons
                if (
                  typeof selectedValue === 'string' &&
                  selectedValue.startsWith('__')
                ) {
                  return;
                }
                setSearchTerm('');
                // Reset show all states when search term changes
                setShowAllSuggestions(false);
                setShowAllProjectContacts(false);
                setShowAllAdditionalContacts(false);
              }}
              onClick={() => {
                if (!dropdownFocus) {
                  setDropdownFocus(true);
                }
              }}
            >
              {renderGroupedOptions()}
            </Select>
          </Form.Item>
        </Form>
        <Modal
          title={t('mail:modals.renameMailAddress.title')}
          visible={isModalVisible}
          onOk={handleOk}
          onCancel={handleCancel}
          okText={t('mail:modals.renameMailAddress.okText')}
          cancelText={t('mail:modals.renameMailAddress.cancelText')}
          okButtonProps={{
            disabled: !debouncedRenameMailinputValue?.match(emailPattern),
          }}
          keyboard
        >
          <Form
            form={modalForm}
            className={classes.flex1}
            onValuesChange={(currentValues) => {
              if (currentValues.renameMailInput) {
                setRenameMailinputValue(currentValues.renameMailInput);
              }
            }}
          >
            <Form.Item
              name="renameMailInput"
              rules={[
                () => ({
                  async validator(rule, value) {
                    if (value.match(emailPattern)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      t('mail:addressBar.errorMessages.incorrectMail')
                    );
                  },
                }),
              ]}
            >
              <Input
                ref={inputRef}
                tabIndex={tabIndex}
                placeholder={selectedMail}
                onPressEnter={() => {
                  handleOk();
                }}
                autoFocus={isModalVisible}
              />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    );
  }
);

export default AddressSelectMail;

interface TagRendererProps extends CustomTagProps {
  showModal: (mail: string) => void;
  closable: boolean;
  selectedValues: string[];
  classes: Classes;
}

const TagRenderer: React.FC<TagRendererProps> = (props) => {
  //#region ------------------------------ Defaults
  const {
    label,
    value,
    closable,
    selectedValues,
    onClose,
    showModal,
    classes,
  } = props;
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  //#endregion

  //#region ------------------------------ Methods / Handlers
  //#endregion

  //#region ------------------------------ Effects
  const [, dragRef, preview] = useDrag({
    type: DND_TYPE_EMAIL_ADDRESS,
    item: { value, label, closable, selectedValues },
    canDrag: () => true,
    end: (_, monitor) => {
      const item = monitor.getDropResult() as any;
      if (item?.close) {
        onClose();
      }
    },
  });

  useEffect(() => {
    preview(getEmptyImage());
  }, [preview]);
  //#endregion

  const emailValue =
    typeof value === 'string' ? value : value?.toString() || '';
  const isInvalidEmail = !emailValue.match(emailPattern);

  return (
    <>
      <div
        className={classNames('ant-select-selection-item', {
          [classes.invalidEmailTag]: isInvalidEmail,
        })}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          showModal(emailValue);
        }}
        ref={dragRef}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        {
          <div
            className={
              isInvalidEmail ? classes.invalidEmailTagContent : undefined
            }
          >
            {label}
          </div>
        }
        {closable && (
          <TransBtn
            className={'ant-select-selection-item-remove'}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            customizeIcon={<CloseOutlined />}
          />
        )}
      </div>
    </>
  );
};
